import { useState } from "react";
import { toast } from "sonner";
import { Clock, Loader2, Mail, MapPin, MessageCircle, Phone, Send } from "lucide-react";
import { EMAIL, PHONE_DISPLAY, SERVICES, waLink } from "../data/content";
import { FadeUp, Overline } from "../components/Reveal";

const encode = (data) =>
  Object.keys(data)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(data[k])}`)
    .join("&");

const InfoCard = ({ Icon, title, children, id }) => (
  <div className="hospitality-card bg-white rounded-3xl border border-line p-7 hover:border-saffron/50 transition-colors" data-testid={id}>
    <span className="w-12 h-12 rounded-2xl bg-saffron-soft flex items-center justify-center">
      <Icon className="w-5 h-5 text-saffron" />
    </span>
    <h3 className="mt-5 font-display font-bold text-lg text-aubergine">{title}</h3>
    <div className="mt-2 text-sm text-ink-muted leading-relaxed">{children}</div>
  </div>
);

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", restaurant: "", service: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setSending(true);
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", ...form }),
      });
      toast.success("Thank you! Our growth team will reach out shortly.");
      setForm({ name: "", phone: "", restaurant: "", service: "", message: "" });
    } catch {
      toast.error("Could not send right now — please reach us on WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-cream border border-line rounded-2xl px-5 py-3.5 text-sm text-ink placeholder:text-ink-muted/60 focus:outline-none focus:ring-2 focus:ring-saffron/50 focus:border-saffron transition-all";

  return (
    <main data-testid="contact-page">
      <section className="hospitality-background relative pt-28 sm:pt-32 lg:pt-40 pb-24 overflow-hidden grain-overlay">
        <div className="absolute top-24 -left-32 w-96 h-96 rounded-full bg-saffron-soft blur-3xl opacity-70 drift-c" />
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-terracotta-soft blur-3xl opacity-70 drift-b" />
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 relative">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <FadeUp y={16}>
                <Overline>Contact Us</Overline>
              </FadeUp>
              <h1 className="mt-5 font-display font-extrabold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-aubergine">
                Let's fill every <span className="text-saffron">table</span> at your venue
              </h1>
              <FadeUp delay={0.15}>
                <p className="mt-6 text-base sm:text-lg text-ink-muted leading-relaxed">
                  Tell us about your restaurant, café, pub or hotel — and what's not working. We'll reply with honest, specific ideas, usually within a few hours.
                </p>
              </FadeUp>
              <FadeUp delay={0.25} className="mt-10 grid sm:grid-cols-2 gap-5">
                <InfoCard Icon={Phone} title="Call / WhatsApp" id="contact-info-phone">
                  <a href={waLink()} data-testid="contact-whatsapp-link" className="font-bold text-aubergine hover:text-saffron transition-colors">{PHONE_DISPLAY}</a>
                  <br />Fastest way to reach us
                </InfoCard>
                <InfoCard Icon={Mail} title="Email" id="contact-info-email">
                  <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="font-bold text-aubergine hover:text-saffron transition-colors break-all">{EMAIL}</a>
                  <br />For detailed briefs
                </InfoCard>
                <InfoCard Icon={MapPin} title="Studio" id="contact-info-location">
                  Bengaluru, Karnataka
                  <br />Serving venues across India
                </InfoCard>
                <InfoCard Icon={Clock} title="Hours" id="contact-info-hours">
                  Mon – Sat
                  <br />9:30 AM – 7:30 PM IST
                </InfoCard>
              </FadeUp>
              <FadeUp delay={0.35} className="mt-8">
                <a
                  href={waLink("Hi GrowMyRestro! I'd like a free audit for my venue.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-quick-whatsapp-button"
                  className="inline-flex items-center gap-3 bg-cardamom text-cream font-bold px-7 py-4 rounded-full hover:bg-aubergine transition-colors duration-300"
                >
                  <MessageCircle className="w-5 h-5" />
                  Quick-connect on WhatsApp
                </a>
              </FadeUp>
            </div>

            <FadeUp delay={0.2}>
              <form
                name="contact"
                onSubmit={submit}
                data-testid="contact-form"
                className="hospitality-card bg-white rounded-[2.5rem] border border-line p-8 sm:p-10 shadow-[0_32px_80px_rgba(45,18,77,0.1)] relative overflow-hidden"
              >
                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-saffron-soft blur-2xl opacity-60" />
                <div className="relative">
                  <span className="font-mono uppercase text-xs tracking-[0.22em] font-semibold text-terracotta">Free Venue Audit</span>
                  <h2 className="mt-3 font-display font-extrabold text-2xl sm:text-3xl text-aubergine">Request your growth audit</h2>
                  <p className="mt-2 text-sm text-ink-muted">We review your Instagram, Google profile, listings and local SEO — free.</p>
                  <div className="mt-8 space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <input
                        data-testid="contact-form-name-input"
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Your name *"
                        className={inputCls}
                      />
                      <input
                        data-testid="contact-form-phone-input"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="Phone / WhatsApp *"
                        className={inputCls}
                      />
                    </div>
                    <input
                      data-testid="contact-form-restaurant-input"
                      value={form.restaurant}
                      onChange={set("restaurant")}
                      placeholder="Restaurant / venue name"
                      className={inputCls}
                    />
                    <select
                      data-testid="contact-form-service-select"
                      value={form.service}
                      onChange={set("service")}
                      className={`${inputCls} ${form.service ? "" : "text-ink-muted/60"}`}
                    >
                      <option value="">Which service are you curious about?</option>
                      {SERVICES.map((s) => (
                        <option key={s.id} value={s.title}>{s.title}</option>
                      ))}
                      <option value="Not sure yet">Not sure yet — surprise me</option>
                    </select>
                    <textarea
                      data-testid="contact-form-message-input"
                      value={form.message}
                      onChange={set("message")}
                      placeholder="What's your biggest challenge right now? (empty weekdays, low orders, an upcoming launch...)"
                      rows={4}
                      className={`${inputCls} resize-none`}
                    />
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="contact-form-submit-button"
                      className="w-full inline-flex items-center justify-center gap-2 bg-aubergine text-cream font-bold px-7 py-4 rounded-full hover:bg-saffron hover:text-aubergine-deep transition-colors duration-300 disabled:opacity-60"
                    >
                      {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />}
                      {sending ? "Sending..." : "Request Free Audit"}
                    </button>
                    <p className="text-center text-xs text-ink-muted">
                      Prefer talking? <a href={waLink()} className="font-bold text-cardamom hover:underline">Message us on WhatsApp</a>
                    </p>
                  </div>
                </div>
              </form>
            </FadeUp>
          </div>
        </div>
      </section>
    </main>
  );
}
